import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { TriArea } from './TriArea';
import { FlexItem } from '../../layout/flexpanel';
import classnames from 'classnames';
import { isReactText, reactNodeIsOfType } from '../../../utils';

/**
 * {@docCategory Toolbar}
 */
export interface ToolbarProps {
   /**
    * The left content.
    */
   leftContent?: ReactNode;

   /**
    * The middle content.
    */
   middleContent?: ReactNode;

   /**
    * The right content.
    */
   rightContent?: ReactNode;

   /**
    * Whether or not to have padding
    *
    * @defaultvalue false
    */
   padding?: boolean;

   /**
    * The vertical alignment of items.
    *
    * @defaultvalue 'center'
    */
   verticalAlign?: 'top' | 'center' | 'bottom';

   /**
    * Sets a className on the outer panel.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    */
   style?: CSSProperties;

   /**
    * The components in the toolbar. This property takes precedence over the left, middle, and right
    * content props.
    */
   children?: ReactNode;
}

export const Toolbar = (props: ToolbarProps) => {
   const className = classnames('uf-toolbar', props.className, {
      'uf-toolbar-padding': props.padding
   });

   if (props.children) {
      return (
         <div
            className={className}
            style={props.style}
         >
            <TriArea
               left={applyFlexItemPropsToChildren(props.children)}
               verticalAlign={props.verticalAlign}
            />
         </div>
      );
   }

   return (
      <div
         className={className}
         style={props.style}
      >
         <TriArea
            left={applyFlexItemPropsToChildren(props.leftContent)}
            middle={applyFlexItemPropsToChildren(props.middleContent)}
            right={applyFlexItemPropsToChildren(props.rightContent)}
            verticalAlign={props.verticalAlign}
         />
      </div>
   );
};

const isFlexItem = (item: React.ReactNode): boolean => {
   return reactNodeIsOfType(item, FlexItem);
};

const applyFlexItemPropsToChildren = (children?: ReactNode): ReactNode => {
   if (reactNodeIsOfType(children, React.Fragment)) {
      const fragmentProps = (children as ReactElement).props;
      const fragmentChildren = fragmentProps.children;

      return applyFlexItemPropsToChildren(fragmentChildren);
   }

   return React.Children.map(children, (child, index) => {
      const reactElement = child as React.ReactElement;

      if (isFlexItem(reactElement)) {
         const childToRender = reactElement.props.children;
         const style: CSSProperties = Object.assign({ ...reactElement.props.style },
                                                    childToRender.props ? childToRender.props.style : {});

         if (reactElement.props.align) {
            style.alignSelf = reactElement.props.align;
         }

         if (reactElement.props.flex) {
            style.flex = reactElement.props.flex;
         }

         if (reactElement.props.order) {
            style.order = reactElement.props.order;
         }

         let classNames = 'uf-flex-item';

         if (reactElement.props.className) {
            classNames += ' ' + reactElement.props.className;
         }

         if (isReactText(childToRender)) {
            return <div style={style} className={classNames}>{childToRender}</div>
         }

         return React.cloneElement(childToRender, { style: style, className: classNames });
      }

      return reactElement;
   });
};
